import { useState, type ChangeEvent } from 'react';
import { planErrorMessage } from '../../components/plan/planErrors';
import { usePlanName } from '../../components/plan/usePlan';
import {
  IMAGE_ACCEPT_ATTRIBUTE,
  assetFileName,
  uploadImageToAssets,
  validateImageFile,
} from '../../lib/storage';
import { AssetPreview } from '../knowledge-base/AssetPreview';
import { assetsApi } from '../knowledge-base/knowledgeBaseApi';
import { useCollection } from '../knowledge-base/useCollection';
import type { TemplatePackFieldsRead, TemplatePackItemBounds } from '../../types/api';
import {
  ITEM_SUBTITLE_MAX_LENGTH,
  ITEM_TITLE_MAX_LENGTH,
  ITEM_VALUE_MAX_LENGTH,
  emptyItemEntry,
  type PackItemEntry,
} from './packItems';

interface PackItemsEditorProps {
  businessId: string;
  /** The pack's field labels — what a row calls its inputs. */
  fields: TemplatePackFieldsRead;
  /** Item-count bounds for the chosen post shape. */
  bounds: TemplatePackItemBounds;
  /** What one entry is, in the pack's own words ("member", "dish"). */
  noun: string;
  entries: PackItemEntry[];
  onChange: (entries: PackItemEntry[]) => void;
  disabled?: boolean;
}

/**
 * The rows a template pack renders from: one row per member, dish or benefit.
 *
 * This is the whole point of the direct-fill path — the user knows the names
 * and the prices, so they type them instead of asking a model to guess. Rows
 * move with the arrow buttons (order is slide order on a carousel and reading
 * order on a single), and a pack that shows pictures gets a per-row pick from
 * the same `assets` collection every other picker reads.
 */
export function PackItemsEditor({
  businessId,
  fields,
  bounds,
  noun,
  entries,
  onChange,
  disabled = false,
}: PackItemsEditorProps) {
  const planName = usePlanName();
  const wantsImages = fields.image_role !== null;
  // Loaded only when a row can actually pick a picture.
  const collection = useCollection(assetsApi, wantsImages ? businessId : null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadTarget, setUploadTarget] = useState<string | null>(null);

  function patch(key: string, changes: Partial<PackItemEntry>) {
    onChange(entries.map((entry) => (entry.key === key ? { ...entry, ...changes } : entry)));
  }

  function move(index: number, delta: -1 | 1) {
    const target = index + delta;
    if (target < 0 || target >= entries.length) return;
    const next = [...entries];
    const [row] = next.splice(index, 1);
    if (row === undefined) return;
    next.splice(target, 0, row);
    onChange(next);
  }

  async function handleUpload(key: string, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;
    const rejected = validateImageFile(file);
    if (rejected) {
      setUploadError(rejected);
      return;
    }
    setUploadError(null);
    setIsUploading(true);
    setUploadTarget(key);
    try {
      const storagePath = await uploadImageToAssets(businessId, file);
      const created = await collection.create({
        storage_path: storagePath,
        kind: 'product_photo',
        tags: [],
        product_id: null,
      });
      patch(key, { asset_id: created.id });
    } catch (caught) {
      setUploadError(planErrorMessage(caught, 'Upload failed. Try again.', { plan: planName }));
    } finally {
      setIsUploading(false);
      setUploadTarget(null);
    }
  }

  const canAdd = entries.length < bounds.max_items;
  const canRemove = entries.length > Math.max(bounds.min_items, 1);
  const isBusy = disabled || isUploading;

  return (
    <fieldset className="pack-items" disabled={disabled}>
      <legend className="field__label">
        {noun.charAt(0).toUpperCase() + noun.slice(1)}s
        <span className="pack-items__bounds">
          {bounds.min_items === bounds.max_items
            ? ` — exactly ${bounds.max_items}`
            : ` — ${bounds.min_items} to ${bounds.max_items}`}
        </span>
      </legend>

      {(uploadError ?? (wantsImages ? collection.error : null)) ? (
        <p className="form__error" role="alert">
          {uploadError ?? collection.error}
        </p>
      ) : null}

      <ol className="pack-items__list">
        {entries.map((entry, index) => {
          const selectedAsset = wantsImages
            ? (collection.items.find((asset) => asset.id === entry.asset_id) ?? null)
            : null;
          return (
            <li className="pack-item" key={entry.key}>
              <div className="pack-item__head">
                <span className="pack-item__index">{index + 1}</span>
                <div className="pack-item__order">
                  <button
                    className="button button--ghost pack-item__move"
                    type="button"
                    aria-label={`Move ${noun} ${index + 1} up`}
                    disabled={isBusy || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    className="button button--ghost pack-item__move"
                    type="button"
                    aria-label={`Move ${noun} ${index + 1} down`}
                    disabled={isBusy || index === entries.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    className="button button--ghost button--danger-text"
                    type="button"
                    disabled={isBusy || !canRemove}
                    onClick={() => onChange(entries.filter((row) => row.key !== entry.key))}
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="pack-item__fields">
                <label className="field">
                  <span className="field__label">{fields.title_label}</span>
                  <input
                    className="field__input"
                    type="text"
                    maxLength={ITEM_TITLE_MAX_LENGTH}
                    value={entry.title}
                    disabled={isBusy}
                    onChange={(event) => patch(entry.key, { title: event.target.value })}
                  />
                </label>

                {fields.subtitle_label ? (
                  <label className="field">
                    <span className="field__label">{fields.subtitle_label}</span>
                    <input
                      className="field__input"
                      type="text"
                      maxLength={ITEM_SUBTITLE_MAX_LENGTH}
                      value={entry.subtitle}
                      disabled={isBusy}
                      onChange={(event) => patch(entry.key, { subtitle: event.target.value })}
                    />
                  </label>
                ) : null}

                {fields.value_label ? (
                  <label className="field pack-item__value">
                    <span className="field__label">{fields.value_label}</span>
                    <input
                      className="field__input"
                      type="text"
                      maxLength={ITEM_VALUE_MAX_LENGTH}
                      placeholder={
                        fields.value_label.toLowerCase().includes('year')
                          ? 'e.g. 3 years'
                          : fields.value_label.toLowerCase().includes('price')
                            ? 'e.g. $24.95'
                            : undefined
                      }
                      value={entry.value}
                      disabled={isBusy}
                      onChange={(event) => patch(entry.key, { value: event.target.value })}
                    />
                    <span className="field__hint">
                      Drawn on the graphic as the achievement callout — include the unit.
                    </span>
                  </label>
                ) : null}
              </div>

              {wantsImages ? (
                <div className="pack-item__image">
                  {selectedAsset ? (
                    <AssetPreview storagePath={selectedAsset.storage_path} />
                  ) : (
                    <span className="pack-item__no-image" aria-hidden="true">
                      {entry.title
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((word) => word.charAt(0).toUpperCase())
                        .join('') || '·'}
                    </span>
                  )}
                  <div className="pack-item__image-actions">
                    <select
                      className="field__input pack-item__asset-select"
                      aria-label={`Photo for ${noun} ${index + 1}`}
                      value={entry.asset_id ?? ''}
                      disabled={isBusy || collection.isLoading}
                      onChange={(event) =>
                        patch(entry.key, { asset_id: event.target.value || null })
                      }
                    >
                      <option value="">No photo — initials placeholder</option>
                      {collection.items.map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {assetFileName(asset.storage_path)}
                        </option>
                      ))}
                    </select>
                    <label
                      className="button button--ghost pack-item__upload"
                      aria-disabled={isBusy}
                    >
                      {isUploading && uploadTarget === entry.key ? 'Uploading' : 'Upload'}
                      <input
                        className="visually-hidden"
                        type="file"
                        accept={IMAGE_ACCEPT_ATTRIBUTE}
                        disabled={isBusy}
                        onChange={(event) => void handleUpload(entry.key, event)}
                      />
                    </label>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="pack-items__actions">
        <button
          className="button"
          type="button"
          disabled={isBusy || !canAdd}
          onClick={() => onChange([...entries, emptyItemEntry()])}
        >
          Add a {noun}
        </button>
        {canAdd ? null : (
          <span className="field__hint">This layout holds {bounds.max_items} at most.</span>
        )}
      </div>
    </fieldset>
  );
}
